import { Link } from 'wouter';
import { ADMIN_ROUTES, ROUTES } from '../../routes/consts';
import cx from 'classix';
import { formatUserFirstLast } from '../../util/TextHelpers';
import { HelpIcon } from '@platform/components';

export const ProfileTabContainer = ({ children }: { children: React.ReactNode }) => {
  return <div className='ml-10 lg:w-[1100px] w-full'>{children}</div>;
};

export const ProfileSection = ({
  title,
  columns = 2,
  children,
}: {
  title: string;
  columns?: number;
  children: React.ReactNode;
}) => {
  return (
    <div className='shadow-xs border border-gray-200 p-2 rounded-lg mb-4'>
      <h3 className='text-lg font-semibold mb-2 '>{title}</h3>
      <div className={`grid grid-cols-${columns} gap-2`}>{children}</div>
    </div>
  );
};

export const ProfileLabel = ({ label, helpText }: { label: string; helpText?: string }) => {
  return (
    <span className='sticky top-0 text-sm text-gray-400'>
      {label}&nbsp;
      {helpText && <HelpIcon supportingText={helpText} size='xs' />}:
    </span>
  );
};

export type ProfileValueColors = 'success' | 'error' | 'warning' | 'default';
const ProfileValue = ({
  value,
  color = 'default',
}: {
  value?: React.ReactNode | string | null;
  color?: ProfileValueColors;
}) => {
  return (
    <span
      className={cx(
        'text-sm',
        color === 'default' && 'text-gray-700 font-medium',
        color !== 'default' && `text-${color}-500 font-semibold`,
      )}
    >
      {value}
    </span>
  );
};
export const ProfileItemContainer = ({
  children,
  extend = false,
}: {
  children: React.ReactNode;
  extend?: boolean;
}) => {
  return (
    <div className={`grid grid-cols-[140px_1fr] gap-1 ${extend ? 'col-span-2' : ''}`}>
      {children}
    </div>
  );
};
export const ProfileItem = ({
  label,
  value,
  color = 'default',
  helpText,
  extend = false,
}: {
  label: string;
  value?: React.ReactNode | string | null;
  color?: ProfileValueColors;
  helpText?: string;
  extend?: boolean;
}) => {
  return (
    <ProfileItemContainer extend={extend}>
      <ProfileLabel label={label} helpText={helpText} />
      <ProfileValue value={value} color={color} />
    </ProfileItemContainer>
  );
};

export const ProfileItemLong = ({
  label,
  value,
  span,
}: {
  label: string;
  value?: string | null;
  span?: number;
}) => {
  return (
    <div
      className={`grid grid-cols-[140px_1fr] gap-1 md:max-w-[75%] md:max-h-[140px] overflow-y-auto items-baseline ${span ? `col-span-${span}` : ''}`}
    >
      <ProfileLabel label={label} />
      <ProfileValue value={value} />
    </div>
  );
};

export const ProfileItemStrList = ({
  label,
  value,
}: {
  label: string;
  value?: string[] | React.ReactNode[] | null;
}) => {
  return (
    <div className='grid grid-cols-[140px_1fr] gap-1 md:max-w-[75%] md:max-h-[140px] overflow-y-auto items-baseline'>
      <ProfileLabel label={label} />
      <ProfileValue
        value={
          value && value.length > 0 ?
            <ul className='list-disc list-outside ml-5'>
              {value.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          : ''
        }
      />
    </div>
  );
};

export const ProfileLinkInternal = ({
  label,
  to,
  displayText,
}: {
  label: string;
  to?: string;
  displayText?: string | null;
}) => {
  return (
    <ProfileItemContainer>
      <ProfileLabel label={label} />
      <ProfileValue
        value={
          to ?
            <Link to={to} className='blue-link text-sm font-medium'>
              {displayText || to}
            </Link>
          : ''
        }
      />
    </ProfileItemContainer>
  );
};

export const ProfileLinkExternal = ({
  label,
  href,
  displayName,
  shorten = true,
}: {
  label: string;
  href?: string | null;
  displayName?: string | null;
  shorten?: boolean;
}) => {
  return (
    <ProfileItemContainer>
      <ProfileLabel label={label} />
      <ProfileValue
        value={
          href ?
            <a
              href={href.startsWith('https://') ? href : `https://${href}`}
              className='blue-link text-sm font-medium'
              target='_blank'
              rel='noopener noreferrer'
            >
              {displayName || (shorten && href.length > 35 ? `${href.substring(0, 35)}...` : href)}
            </a>
          : ''
        }
      />
    </ProfileItemContainer>
  );
};

export const ProUserProfileLink = ({
  userId,
  firstname,
  lastname,
}: {
  userId?: number | null;
  firstname?: string | null;
  lastname?: string | null;
}) => {
  return (
    <ProfileLinkInternal
      label='Professional'
      to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.PROFESSIONALS}/${userId}`}
      displayText={formatUserFirstLast(firstname, lastname)}
    />
  );
};

export const OrganizationProfileLink = ({
  organizationId,
  organizationName,
}: {
  organizationId?: number | null;
  organizationName?: string | null;
}) => {
  return (
    <ProfileLinkInternal
      label='Organization'
      to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.ORGANIZATIONS}/${organizationId}`}
      displayText={organizationName}
    />
  );
};

export const ProjectProfileLink = ({
  projectId,
  projectName,
}: {
  projectId?: number | null;
  projectName?: string | null;
}) => {
  return (
    <ProfileLinkInternal
      label='Project'
      to={projectId ? `~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.PROJECTS}/${projectId}` : undefined}
      displayText={projectName}
    />
  );
};

export const TeamProfileLink = ({
  cohortId,
  teamId,
}: {
  cohortId?: number | null;
  teamId?: number | null;
}) => {
  return (
    <ProfileLinkInternal
      label='Team'
      to={
        cohortId && teamId ?
          `~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.TEAMS}/${cohortId}/${teamId}`
        : undefined
      }
      displayText={teamId?.toString()}
    />
  );
};

export const CorporateDashboardProfileLink = ({
  corporateClientId,
  corporateClientName,
  cohortId,
}: {
  corporateClientId?: number | null;
  corporateClientName?: string | null;
  cohortId?: number | null;
}) => {
  const url = `~/platform${ROUTES.CORPORATE_DASHBOARD}?corporateClientId=${corporateClientId}${cohortId ? `&cohortId=${cohortId}` : ''}`;
  return <ProfileLinkInternal label='CSR Dashboard' to={url} displayText={corporateClientName} />;
};

export const NoRecordsFoundTabContent = ({ recordType }: { recordType: string }) => {
  return <div className='text-lg text-center mt-4'>No records found for this {recordType}.</div>;
};
